import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { Add, DeleteOutlined } from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  EntityColumnNamePicker,
  useEntityContext
} from '@sierralabs/entity-ui';
import { State, WorkflowInvoke } from '@sierralabs/workflow';
import {
  findTaskEntity,
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin,
  TaskContext,
  useFindTaskTypeById
} from '@sierralabs/workflow-ui';

import { FetchError } from '../../../../common/api/api';
import { environmentV2 } from '../../../../environments';
import JiraIssueFieldPicker from '../../../jira-connect/pickers/JiraIssueFieldPicker';
import JiraIssueTypePicker from '../../../jira-connect/pickers/JiraIssueTypePicker';
import JiraProjectPicker from '../../../jira-connect/pickers/JiraProjectPicker';
import { useJiraConnectContext } from '../../../jira-connect/JiraConnect.context';
import {
  createJiraIssueFields,
  getJiraIssueEntityFieldOptions,
  useUpdateJiraIssues
} from '../../../jira-connect/JiraConnect.hooks';
import { findTaskJiraIssuesV2 } from '../../../task-jira-v2/TaskJiraIssueV2.hooks';
import { TaskV2 } from '../../../task-v2/TaskV2.interface';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .MuiTableHead-root .MuiTableCell-root {
    padding-left: 4px;
    padding-right: 4px;
  }
  .MuiTableRow-root.dense .MuiTableCell-root {
    padding: 2px 4px;
  }
`;

export interface UpdateJiraIssueParams {
  src: 'updateJiraIssue';
  data: {
    projectId?: string;
    fields?: {
      /** Entity column property name. */
      entityField?: string;
      issueType?: { id: string; name: string };
      issueField?: string;
    }[];
  };
}

const UpdateJiraIssueConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { taskTypeId, onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  const { entities } = useEntityContext();
  const { data: taskType, get } = useFindTaskTypeById(taskTypeId || 0);
  if (!invoke.data) invoke.data = {};
  const data: UpdateJiraIssueParams['data'] = invoke.data;
  if (!data.fields) data.fields = [];
  const fields = data.fields;

  const options = useMemo(() => {
    const entity =
      taskType &&
      entities?.find(schema => {
        return schema.id && taskType.entityId
          ? schema.id === taskType.entityId
          : taskType.entityTable && schema.tableName === taskType.entityTable;
      });
    return getJiraIssueEntityFieldOptions({
      entities,
      entity,
      taskSchemaName: environmentV2.entities?.task
    });
  }, [entities, taskType]);

  const update = (data: UpdateJiraIssueParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  useEffect(() => {
    get();
  }, [get]);

  return (
    <StyledConfiguratorDiv>
      <JiraProjectPicker
        value={data.projectId}
        textFieldProps={{ size: 'small' }}
        onChange={projectId => update({ projectId, fields: [] })}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Entity Field</TableCell>
              <TableCell>Issue Type</TableCell>
              <TableCell>Issue Field</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.fields.map((item, index) => (
              <TableRow key={index} className='dense'>
                <TableCell>
                  <EntityColumnNamePicker
                    options={options}
                    value={item.entityField}
                    disabled={!data.projectId}
                    textFieldProps={{
                      size: 'small',
                      InputLabelProps: { style: { fontSize: 10 } }
                    }}
                    onChange={propertyName => {
                      // NOTE: passed in options prop uses property name instead of columnName
                      fields[index].entityField = propertyName || '';
                      update({ ...data, fields });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <JiraIssueTypePicker
                    value={item.issueType?.id}
                    disabled={!item.entityField}
                    textFieldProps={{
                      size: 'small',
                      InputLabelProps: { style: { fontSize: 10 } }
                    }}
                    onChange={issueType => {
                      fields[index].issueType = issueType && {
                        id: issueType.id,
                        name: issueType.label
                      };
                      update({ ...data, fields });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <JiraIssueFieldPicker
                    value={item.issueField}
                    projectId={data.projectId || ''}
                    issueTypeId={item.issueType?.id || ''}
                    disabled={!item.issueType?.id}
                    textFieldProps={{
                      size: 'small',
                      InputLabelProps: { style: { fontSize: 10 } }
                    }}
                    onChange={issueField => {
                      fields[index].issueField = issueField;
                      update({ ...data, fields });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size='small'
                    onClick={() => {
                      fields.splice(index, 1);
                      update({ ...data, fields });
                    }}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={2}>
        <Button
          color='primary'
          variant='outlined'
          startIcon={<Add />}
          onClick={() => {
            fields.push({});
            update({ ...data, fields });
          }}
        >
          Add Item
        </Button>
      </Box>
    </StyledConfiguratorDiv>
  );
};

const UpdateJiraIssueDisplay: React.FC<ServiceDisplayProps> = () => {
  return null;
};

const StyledDialogContent = styled(DialogContent)`
  .MuiTypography-root,
  .MuiAlert-root {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

export interface UpdateJiraIssueDialogProps {
  open: boolean;
  data: UpdateJiraIssueParams['data'];
  state: State<TaskContext>;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const UpdateJiraIssueDialog: React.FC<UpdateJiraIssueDialogProps> = props => {
  const { open, data, state, onCancel, onSuccess } = props;
  const stateRef = useRef(state);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const [error, setError] = useState<FetchError>();
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<React.ReactNode>();
  const { isAvailable } = useJiraConnectContext();
  const [updateResult, updateIssues] = useUpdateJiraIssues();
  const task = stateRef.current.context.task as TaskV2;
  const alertMsg = !isAvailable
    ? 'Unable to run action and update Jira issue(s). ' +
      'You can choose to skip this action, or open SierraQMS ' +
      'within JIRA and try again to properly run this action.'
    : alertMessage;
  const loading = isLoading || updateResult.isLoading;

  const handleCancel = () => {
    if (!loading) {
      onCancel?.();
    }
  };

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    const dataFields = data.fields || [];
    if (!isAvailable || dataFields.length === 0) {
      return;
    }
    const apiTaskEntity = findTaskEntity(task.id);
    const apiJiraIssues = findTaskJiraIssuesV2(task.id);
    const run = async () => {
      setIsLoading(true);
      const [
        { error: taskEntityError, data: taskEntity },
        { error: jiraIssuesError, data: jiraIssues }
      ] = await Promise.all([apiTaskEntity.result, apiJiraIssues.result]);
      setIsLoading(false);
      setError(taskEntityError || jiraIssuesError);
      if (!taskEntity || !jiraIssues || jiraIssues.length === 0) {
        const selectedIssueTypes = dataFields
          .map(item => `"${item.issueType?.name}"`)
          .join(', ');
        setAlertMessage(
          !taskEntity
            ? 'Unable to load the Task entity. You can choose to skip this action.'
            : 'There are no linked JIRA issues that match some of the ' +
                `issue type(s): ${selectedIssueTypes}, please link an issue that ` +
                'matches the given issue type(s) to proceed. You can choose to skip this action.'
        );
        return;
      }
      setAlertMessage(undefined);
      const filteredJiraIssues = jiraIssues.filter(jiraIssue => {
        return dataFields.some(
          item => item.issueType?.id === jiraIssue.issue.fields.issuetype.id
        );
      });
      const updates = filteredJiraIssues.map(issue => {
        const allFields = dataFields.filter(
          field => field.issueType?.id === issue.issue.fields.issuetype.id
        );
        const fields = createJiraIssueFields(taskEntity, allFields);
        return { issueId: issue.issue.id, dto: { fields } };
      });
      updateIssues(updates);
    };
    run();
    return () => {
      apiTaskEntity.request.abort();
      apiJiraIssues.request.abort();
    };
  }, [data, isAvailable, task.id, updateIssues]);

  useEffect(() => {
    if (updateResult.success) {
      onSuccessRef.current?.();
    }
  }, [updateResult.success]);

  return (
    <Dialog open={open} onClose={handleCancel}>
      {loading && <LinearProgress />}
      <DialogTitle>Update Jira Issue</DialogTitle>
      <StyledDialogContent>
        {loading && <Typography>Updating Jira Issue(s)...</Typography>}
        {alertMsg && (
          <Alert severity='warning'>
            <AlertTitle>
              {!isAvailable
                ? 'JIRA Connect API is not available'
                : 'Unable to update JIRA issue(s)'}
            </AlertTitle>
            {alertMsg}
          </Alert>
        )}
        {updateResult.errors.length > 0 && (
          <Alert severity='error'>
            {updateResult.errors.map((error, index) => (
              <div key={index}>{error.message}</div>
            ))}
          </Alert>
        )}
        {error && <Alert severity='error'>{error.message}</Alert>}
      </StyledDialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={handleCancel}>
          Cancel
        </Button>
        {alertMsg && (
          <Button
            color='primary'
            variant='outlined'
            disabled={loading}
            onClick={() => onSuccess?.()}
          >
            Skip Action and Proceed
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const UpdateJiraIssuePlugin: ServicePlugin<UpdateJiraIssueParams> = {
  src: 'updateJiraIssue',
  name: 'Update Jira Issue',
  configurator: UpdateJiraIssueConfigurator,
  display: UpdateJiraIssueDisplay,
  validate: (condition: Partial<WorkflowInvoke>) => {
    const data = condition.data as UpdateJiraIssueParams['data'];
    return !!(
      data &&
      data.projectId &&
      Array.isArray(data.fields) &&
      data.fields.every(
        field => field.entityField && field.issueField && field.issueType
      )
    );
  },
  service: (state: State<TaskContext>, params, render) => {
    console.log('updateJiraIssue params', params);
    const pluginParams = Array.isArray(params) ? params[0] : params;
    return new Promise((resolve, reject) => {
      render?.(
        <UpdateJiraIssueDialog
          open
          data={pluginParams.data}
          state={state}
          onSuccess={() => {
            render?.(undefined);
            resolve(true);
          }}
          onCancel={() => {
            render?.(undefined);
            reject(true);
          }}
        />
      );
    });
  }
};
